import React, { useState, useEffect } from "react";
import { Container, Row, Col, Input, Label, Button } from "reactstrap";
import { useSelector } from "react-redux";
import Chart from "react-apexcharts";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  GetAllOrders,
  GetMyStoreOrders,
  GetDeliveryBoys,
  GetPromotions,
  GetStores,
  GetProducts
} from "../../requests";
import classes from "./styles.module.css";

// order.orderedAt is written server-side as a locale string, e.g.
// "19/7/2026, 6:12 pm" (DateTime.DATETIME_SHORT in Asia/Kolkata — see
// OrderService.js's createdAtIST). It's DD/M/YYYY, not ISO, and the comma
// before the time means naively splitting on " " left a trailing comma on
// the date ("19/7/2026,"), which new Date() can't parse — every date-range
// comparison against it silently evaluated to NaN and matched nothing, no
// matter which date filter was selected. This converts to a real
// YYYY-MM-DD string, directly comparable with the ISO strings the date
// pickers already produce.
// order.orderedAt is generated server-side via Luxon's DATETIME_SHORT preset
// under the en-US locale (OrderService.js), which formats as M/D/YYYY — NOT
// D/M/YYYY. This was previously destructured as [day, month, year], which
// silently swapped the two: any day-of-month > 12 produced an invalid month
// number that matched no date filter at all ("No Data Found" despite real
// sales), and any day-of-month <= 12 silently misattributed the order to a
// different, wrong-but-valid date instead of erroring.
// Date.toISOString() always converts to UTC, which shifts the calendar date
// for anyone in IST between midnight and 5:30 AM (and generally whenever
// local-date != UTC-date). The "Today"/"Yesterday"/"This Week"/"This Month"
// filters below need the browser's LOCAL calendar date instead, to match how
// orderedAtToISODate above derives order dates from the IST-formatted
// orderedAt string.
function toLocalISODate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function orderedAtToISODate(orderedAt) {
  if (!orderedAt) return null;
  const datePart = orderedAt.split(",")[0]?.trim();
  if (!datePart) return null;
  const [month, day, year] = datePart.split("/").map(Number);
  if (!day || !month || !year) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const Reports = ({ activeTab: initialTab }) => {
  const [activeTab, setActiveTab] = useState(initialTab || "daily");
  const [loading, setLoading] = useState(true);

  // Data states
  const [orders, setOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);

  // Date Filter Preset state
  const [dateOption, setDateOption] = useState("today"); // today, yesterday, thisWeek, thisMonth, custom, range, allTime

  // Custom / Range Date Pickers
  const [singleDate, setSingleDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Store select
  const [selectedStore, setSelectedStore] = useState("all");

  const [dailyLogPage, setDailyLogPage] = useState(1);
  const rowsPerPage = 10;

  const userRole = useSelector((state) => state?.login?.role);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // 1. Fetch Orders based on role
      // NOTE: axios's default response interceptor (registered globally in
      // helpers/api_helper.js, active on every protected page via
      // AuthProtected -> UserHooks -> api_helper) already rewrites every
      // response to response.data before it reaches here — so ordersResp
      // IS the {success, data, error} body already, and .data on it is the
      // real array. An earlier pass at this file mistakenly "fixed" this to
      // .data.data, which double-unwraps into undefined — reverted.
      let ordersResp;
      if (userRole === "admin") {
        ordersResp = await GetMyStoreOrders(token);
      } else {
        ordersResp = await GetAllOrders(token);
      }
      setOrders(ordersResp?.data || []);

      // 2. Fetch Delivery Boys
      try {
        const ridersResp = await GetDeliveryBoys(token);
        setDeliveryBoys(ridersResp?.data || []);
      } catch (err) {
        console.error("Failed to fetch delivery boys:", err);
      }

      // 3. Fetch Promotions
      try {
        const promoResp = await GetPromotions(token);
        setPromotions(promoResp?.data || []);
      } catch (err) {
        console.error("Failed to fetch promotions:", err);
      }

      // 4. Fetch Stores
      try {
        const storesResp = await GetStores();
        setStores(storesResp?.data || []);
      } catch (err) {
        console.error("Failed to fetch stores:", err);
      }

      // 5. Fetch Products
      try {
        const prodResp = await GetProducts();
        setProducts(prodResp?.data?.data || prodResp?.data || []);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userRole]);

  useEffect(() => {
    setDailyLogPage(1);
  }, [activeTab, dateOption, selectedStore, startDate, endDate, singleDate]);

  // Date filtering logic
  const getFilteredOrders = () => {
    return orders.filter((order) => {
      // 1. Store Filter Check
      const matchesStore = selectedStore === "all" || order.store?.name === selectedStore;
      if (!matchesStore) return false;

      // 2. Date Filter Check
      if (dateOption === "allTime") return true;

      const orderDateStr = orderedAtToISODate(order.orderedAt);
      if (!orderDateStr) return false;

      if (dateOption === "custom") {
        return orderDateStr === singleDate;
      }

      let computedStart = startDate;
      let computedEnd = endDate;

      if (dateOption === "today") {
        const todayStr = toLocalISODate(new Date());
        computedStart = todayStr;
        computedEnd = todayStr;
      } else if (dateOption === "yesterday") {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        const yesterdayStr = toLocalISODate(d);
        computedStart = yesterdayStr;
        computedEnd = yesterdayStr;
      } else if (dateOption === "thisWeek") {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const distance = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // distance to Monday
        const monday = new Date(today);
        monday.setDate(today.getDate() - distance);
        computedStart = toLocalISODate(monday);
        computedEnd = toLocalISODate(today);
      } else if (dateOption === "thisMonth") {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        computedStart = toLocalISODate(firstDay);
        computedEnd = toLocalISODate(today);
      }

      const orderTime = new Date(orderDateStr).getTime();
      const startTime = new Date(computedStart).getTime();
      const endTime = new Date(computedEnd).getTime();

      return orderTime >= startTime && orderTime <= endTime;
    });
  };

  const filteredOrders = getFilteredOrders();
  const deliveredOrders = filteredOrders.filter(
    (o) => (o.status || "").toLowerCase() === "delivered"
  );

  // Tabs layout configuration
  const tabs = [
    { id: "daily", label: "Sales Overview", icon: "mdi mdi-chart-timeline-variant" },
    { id: "store", label: "Store Performance", icon: "mdi mdi-store" },
    { id: "bestsellers", label: "Best Sellers", icon: "mdi mdi-pizza" },
    { id: "delivery", label: "Delivery Report", icon: "mdi mdi-truck-delivery" },
    { id: "promotion", label: "Promotion Report", icon: "mdi mdi-tag-multiple" }
  ];

  // CSV Exporter Engine
  const exportToCSV = () => {
    let csvRows = [];
    let filename = `MVP_Report_${activeTab}_${dateOption}.csv`;

    if (activeTab === "daily") {
      csvRows.push(["Date", "Orders", "Sales (₹)", "Avg Order (₹)"]);
      dailyData.dates.forEach((date, idx) => {
        const count = dailyData.counts[idx];
        const sales = dailyData.sales[idx];
        const avg = count ? Math.round(sales / count) : 0;
        csvRows.push([date, count, sales, avg]);
      });
    } else if (activeTab === "store") {
      csvRows.push(["Store Name", "Orders", "Revenue (₹)", "Avg Order (₹)"]);
      storeSalesData.forEach((store) => {
        const avg = store.orders ? Math.round(store.sales / store.orders) : 0;
        csvRows.push([store.name, store.orders, store.sales, avg]);
      });
    } else if (activeTab === "bestsellers") {
      csvRows.push(["Product Name", "Quantity Sold", "Revenue (₹)"]);
      bestSellersData.forEach((product) => {
        csvRows.push([product.name, product.quantity, product.revenue]);
      });
    } else if (activeTab === "delivery") {
      csvRows.push(["Rider Name", "Deliveries Assigned", "Failed Deliveries", "Success Rate (%)"]);
      deliveryStats.riders.forEach((rider) => {
        csvRows.push([rider.name, rider.total, rider.failed, `${rider.rate}%`]);
      });
    } else if (activeTab === "promotion") {
      csvRows.push(["Promotion Name", "Promo Code", "Usage Count", "Total Discount Applied (₹)", "Status"]);
      promoStats.list.forEach((promo) => {
        csvRows.push([promo.name, promo.code, promo.usage, promo.discount, promo.status]);
      });
    }

    if (csvRows.length === 0) return;

    const csvContent = csvRows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── 1. SALES OVERVIEW CALCULATIONS ──────────────────────────────────────────
  const getDailyData = () => {
    const dailyMap = {};
    deliveredOrders.forEach((o) => {
      const date = orderedAtToISODate(o.orderedAt) || "N/A";
      if (!dailyMap[date]) {
        dailyMap[date] = { sales: 0, count: 0 };
      }
      dailyMap[date].sales += o.totalprice || 0;
      dailyMap[date].count += 1;
    });

    const sortedDates = Object.keys(dailyMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    const salesSeries = sortedDates.map((d) => dailyMap[d].sales);
    const countSeries = sortedDates.map((d) => dailyMap[d].count);

    return { dates: sortedDates, sales: salesSeries, counts: countSeries };
  };

  const getDailyDataNormalized = () => {
    if (deliveredOrders.length === 0) {
      return { dates: [], sales: [], counts: [], bucketType: "day" };
    }

    const sortedOrders = [...deliveredOrders].sort((a, b) => {
      const dateA = new Date(orderedAtToISODate(a.orderedAt)).getTime() || 0;
      const dateB = new Date(orderedAtToISODate(b.orderedAt)).getTime() || 0;
      return dateA - dateB;
    });

    const oldestDateStr = orderedAtToISODate(sortedOrders[0]?.orderedAt);
    const newestDateStr = orderedAtToISODate(sortedOrders[sortedOrders.length - 1]?.orderedAt);

    let diffDays = 30;
    if (oldestDateStr && newestDateStr) {
      const oldestTime = new Date(oldestDateStr).getTime();
      const newestTime = new Date(newestDateStr).getTime();
      diffDays = Math.round((newestTime - oldestTime) / (1000 * 60 * 60 * 24));
    }

    let bucketType = "day";
    if (diffDays > 180) {
      bucketType = "month";
    } else if (diffDays > 30) {
      bucketType = "week";
    }

    const groupMap = {};

    sortedOrders.forEach((o) => {
      const dateStr = orderedAtToISODate(o.orderedAt);
      if (!dateStr) return;

      const dateObj = new Date(dateStr);
      let key = dateStr;

      if (bucketType === "month") {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        key = `${year}-${month}`;
      } else if (bucketType === "week") {
        const day = dateObj.getDay();
        const diff = dateObj.getDate() - day + (day === 0 ? -6 : 1);
        const mondayObj = new Date(dateObj.setDate(diff));
        key = `Wk ${mondayObj.toISOString().split("T")[0]}`;
      }

      if (!groupMap[key]) {
        groupMap[key] = { sales: 0, count: 0 };
      }
      groupMap[key].sales += o.totalprice || 0;
      groupMap[key].count += 1;
    });

    const keys = Object.keys(groupMap).sort((a, b) => {
      if (bucketType === "month" || bucketType === "week") {
        return a.localeCompare(b);
      }
      return new Date(a).getTime() - new Date(b).getTime();
    });

    const salesSeries = keys.map((k) => groupMap[k].sales);
    const countSeries = keys.map((k) => groupMap[k].count);

    return { dates: keys, sales: salesSeries, counts: countSeries, bucketType };
  };

  const dailyData = getDailyData();
  const normalizedData = getDailyDataNormalized();

  // Pagination for Daily Sales Logs table
  const totalDailyLogs = dailyData.dates.length;
  const totalPages = Math.ceil(totalDailyLogs / rowsPerPage);
  const paginatedDates = dailyData.dates.slice(
    (dailyLogPage - 1) * rowsPerPage,
    dailyLogPage * rowsPerPage
  );

  const totalSalesVal = deliveredOrders.reduce((sum, o) => sum + (o.totalprice || 0), 0);
  const totalOrdersVal = filteredOrders.length;
  const deliveredOrdersVal = deliveredOrders.length;
  const avgOrderVal = deliveredOrdersVal ? totalSalesVal / deliveredOrdersVal : 0;

  // Trend Charts Configs
  const salesTrendOptions = {
    chart: { id: "sales-trend", toolbar: { show: false } },
    xaxis: { categories: normalizedData.dates },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#f57c00"],
    tooltip: { y: { formatter: (val) => `₹${val.toLocaleString()}` } }
  };
  const salesTrendSeries = [{ name: "Sales (₹)", data: normalizedData.sales }];

  const ordersTrendOptions = {
    chart: { id: "orders-trend", toolbar: { show: false } },
    xaxis: { categories: normalizedData.dates },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#227ae6"],
  };
  const ordersTrendSeries = [{ name: "Orders", data: normalizedData.counts }];

  // ─── 2. STORE PERFORMANCE CALCULATIONS ───────────────────────────────────────
  const getStoreSalesData = () => {
    const storeMap = {};
    deliveredOrders.forEach((o) => {
      const storeName = o.store?.name || "N/A";
      if (!storeMap[storeName]) {
        storeMap[storeName] = { sales: 0, count: 0 };
      }
      storeMap[storeName].sales += o.totalprice || 0;
      storeMap[storeName].count += 1;
    });

    const sortedStores = Object.keys(storeMap).sort(
      (a, b) => storeMap[b].sales - storeMap[a].sales
    );

    return sortedStores.map((s, idx) => ({
      rank: idx + 1,
      name: s,
      sales: storeMap[s].sales,
      orders: storeMap[s].count
    }));
  };

  const storeSalesData = getStoreSalesData();
  const highestRevenueStore = storeSalesData[0]?.name || "N/A";
  const totalStoresCount = userRole === "admin" ? 1 : stores.length;
  const avgStoreRevenue = totalStoresCount ? totalSalesVal / totalStoresCount : 0;

  const storeCompareChartOptions = {
    chart: { id: "store-compare", toolbar: { show: false } },
    xaxis: { categories: storeSalesData.map((s) => s.name) },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "40%" } },
    colors: ["#227ae6"],
    tooltip: { y: { formatter: (val) => `₹${val.toLocaleString()}` } }
  };
  const storeCompareChartSeries = [
    { name: "Revenue (₹)", data: storeSalesData.map((s) => s.sales) }
  ];

  // ─── 3. BEST SELLERS CALCULATIONS ────────────────────────────────────────────
  const getBestSellersData = () => {
    const itemMap = {};
    deliveredOrders.forEach((o) => {
      if (o.items) {
        o.items.forEach((item) => {
          const name = item.name || "N/A";
          if (!itemMap[name]) {
            itemMap[name] = { quantity: 0, revenue: 0 };
          }
          itemMap[name].quantity += item.quantity || 0;

          // Resolve estimated product price from products catalog list
          const prodObj = products.find((p) => p.name === item.name);
          const price = prodObj ? (prodObj.price || 0) : 150; // default to 150 if not found
          itemMap[name].revenue += (item.quantity || 0) * price;
        });
      }
    });

    return Object.keys(itemMap)
      .map((name) => ({
        name,
        quantity: itemMap[name].quantity,
        revenue: itemMap[name].revenue
      }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);
  };

  const bestSellersData = getBestSellersData();
  const topSellingProduct = bestSellersData[0]?.name || "N/A";
  const unitsSoldVal = bestSellersData.reduce((sum, item) => sum + item.quantity, 0);

  const bestSellersChartOptions = {
    chart: { id: "bestsellers-chart", toolbar: { show: false } },
    xaxis: { categories: bestSellersData.map((b) => b.name) },
    plotOptions: { bar: { borderRadius: 6, horizontal: true } },
    colors: ["#37b24d"]
  };
  const bestSellersChartSeries = [
    { name: "Quantity Sold", data: bestSellersData.map((b) => b.quantity) }
  ];

  // ─── 4. DELIVERY REPORT CALCULATIONS ──────────────────────────────────────────
  const getDeliveryStats = () => {
    const deliveryOrders = filteredOrders.filter((o) => o.method === "delivery");
    const totalDeliveries = deliveryOrders.length;
    const successful = deliveryOrders.filter(
      (o) => (o.status || "").toLowerCase() === "delivered"
    ).length;
    const failed = deliveryOrders.filter(
      (o) => (o.status || "").toLowerCase() === "delivery_failed"
    ).length;

    // Rider list aggregator
    const riderMap = {};
    deliveryOrders.forEach((o) => {
      const riderName = o.deliveryBoy?.name;
      if (riderName) {
        if (!riderMap[riderName]) {
          riderMap[riderName] = { completed: 0, failed: 0, total: 0 };
        }
        riderMap[riderName].total += 1;
        if ((o.status || "").toLowerCase() === "delivered") {
          riderMap[riderName].completed += 1;
        } else if ((o.status || "").toLowerCase() === "delivery_failed") {
          riderMap[riderName].failed += 1;
        }
      }
    });

    const ridersList = Object.keys(riderMap).map((rider) => ({
      name: rider,
      ...riderMap[rider],
      rate: riderMap[rider].total
        ? Math.round((riderMap[rider].completed / riderMap[rider].total) * 100)
        : 0
    }));

    return {
      total: totalDeliveries,
      successful,
      failed,
      successRate: totalDeliveries ? Math.round((successful / totalDeliveries) * 100) : 0,
      riders: ridersList
    };
  };

  const deliveryStats = getDeliveryStats();

  const deliveryChartOptions = {
    labels: ["Successful", "Failed", "In Progress"],
    colors: ["#37b24d", "#e31b23", "#ffc078"],
    legend: { position: "bottom" }
  };
  const deliveryChartSeries = [
    deliveryStats.successful,
    deliveryStats.failed,
    deliveryStats.total - (deliveryStats.successful + deliveryStats.failed)
  ];

  // ─── 5. PROMOTION REPORT CALCULATIONS ─────────────────────────────────────────
  const getPromotionStats = () => {
    const promoMap = {};

    if (promotions && promotions.length > 0) {
      promotions.forEach((promo) => {
        const displayName = promo.name || "Unnamed Promotion";
        const identifier = promo.id || promo.code || displayName;
        promoMap[identifier] = {
          name: displayName,
          code: promo.code || "Auto Applied",
          usage: 0,
          discount: 0,
          status: promo.status || "active"
        };
      });
    }

    const GENERAL_PROMO_KEY = "general_promo";
    promoMap[GENERAL_PROMO_KEY] = {
      name: "General Promotion",
      code: "Various",
      usage: 0,
      discount: 0,
      status: "active"
    };

    filteredOrders.forEach((o) => {
      if (o.discountAmount > 0) {
        // Coupon-code orders carry couponsUsed.id; orders discounted by an
        // automatically-applied promo (no code entered) carry
        // appliedAutomaticPromo.id instead — only recorded server-side
        // recently (OrderService.js), so older orders placed before that
        // still fall back to the General Promotion catch-all below.
        const orderPromoId = o.couponsUsed?.id || o.appliedAutomaticPromo?.id;
        let matchedKey = null;
        if (orderPromoId) {
          if (promoMap[orderPromoId]) {
            matchedKey = orderPromoId;
          } else {
            const foundPromo = promotions.find(
              (p) => p.code === orderPromoId || p.id === orderPromoId
            );
            if (foundPromo) {
              matchedKey = foundPromo.id || foundPromo.code || foundPromo.name;
            }
          }
        }

        const targetKey = matchedKey || GENERAL_PROMO_KEY;
        if (promoMap[targetKey]) {
          promoMap[targetKey].usage += 1;
          promoMap[targetKey].discount += o.discountAmount || 0;
        }
      }
    });

    const totalDiscount = filteredOrders.reduce((sum, o) => sum + (o.discountAmount || 0), 0);
    const promotionList = Object.keys(promoMap)
      .map((key) => ({
        id: key,
        ...promoMap[key]
      }))
      .filter((p) => p.id !== GENERAL_PROMO_KEY || p.usage > 0);

    promotionList.sort((a, b) => b.discount - a.discount);
    const topCoupon = promotionList[0]?.name || "N/A";
    const promoUsageCount = filteredOrders.filter((o) => o.discountAmount > 0).length;

    return {
      totalDiscount,
      topCoupon,
      promoUsageCount,
      list: promotionList
    };
  };

  const promoStats = getPromotionStats();

  const promoChartOptions = {
    chart: { id: "promotion-chart", toolbar: { show: false } },
    xaxis: { categories: promoStats.list.map((p) => p.name) },
    plotOptions: { bar: { borderRadius: 6 } },
    colors: ["#ae3ec9"]
  };
  const promoChartSeries = [
    { name: "Total Usage Count", data: promoStats.list.map((p) => p.usage) }
  ];

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className={`page-content ${classes.reportsDashboard}`}>
        <Container fluid={true}>
          <Breadcrumbs title="Reports" breadcrumbItem={tabs.find((t) => t.id === activeTab)?.label || "Reports"} />

          {/* Tab Navigation */}
          <div className={classes.tabContainer}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${classes.tabItem} ${activeTab === tab.id ? classes.tabItemActive : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon} style={{ fontSize: "1.1rem" }}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* MVP Common Filter Bar */}
          <div className={classes.filterCard}>
            <div className={classes.filterRow} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', flex: 1 }}>

                {/* Date Dropdown Preset */}
                <div className={classes.filterGroup} style={{ minWidth: '160px', flex: 'none' }}>
                  <Label for="dateOption">Date Filter</Label>
                  <Input
                    type="select"
                    id="dateOption"
                    className={classes.filterInput}
                    value={dateOption}
                    onChange={(e) => setDateOption(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="thisWeek">This Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="custom">Custom Date</option>
                    <option value="range">Date Range</option>
                    <option value="allTime">All Time</option>
                  </Input>
                </div>

                {/* Conditional Single Date Picker */}
                {dateOption === "custom" && (
                  <div className={classes.filterGroup} style={{ minWidth: '150px', flex: 'none' }}>
                    <Label for="singleDate">Select Date</Label>
                    <Input
                      type="date"
                      id="singleDate"
                      className={classes.filterInput}
                      value={singleDate}
                      onChange={(e) => setSingleDate(e.target.value)}
                    />
                  </div>
                )}

                {/* Conditional Range Date Pickers */}
                {dateOption === "range" && (
                  <>
                    <div className={classes.filterGroup} style={{ minWidth: '140px', flex: 'none' }}>
                      <Label for="startDate">Start Date</Label>
                      <Input
                        type="date"
                        id="startDate"
                        className={classes.filterInput}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div className={classes.filterGroup} style={{ minWidth: '140px', flex: 'none' }}>
                      <Label for="endDate">End Date</Label>
                      <Input
                        type="date"
                        id="endDate"
                        className={classes.filterInput}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Store Filter Selector */}
                {userRole !== "admin" && (
                  <div className={classes.filterGroup} style={{ minWidth: '160px', flex: 'none' }}>
                    <Label for="storeSelect">Store Select</Label>
                    <Input
                      type="select"
                      id="storeSelect"
                      className={classes.filterInput}
                      value={selectedStore}
                      onChange={(e) => setSelectedStore(e.target.value)}
                    >
                      <option value="all">All Stores</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.name}>
                          {store.name}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}
              </div>

              {/* Action Buttons (Refresh & CSV Export) */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <Button color="light" onClick={fetchData} title="Refresh Data">
                  <i className="mdi mdi-refresh me-1"></i> Refresh
                </Button>
                <Button color="primary" onClick={exportToCSV} title="Export Table as CSV">
                  <i className="mdi mdi-download me-1"></i> Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Main Body Grid */}
          {filteredOrders.length === 0 ? (
            <div className={classes.tableCard} style={{ padding: "4rem 2rem", textAlign: "center" }}>
              <i className="mdi mdi-database-off" style={{ fontSize: "3.5rem", color: "#ced4da" }}></i>
              <h4 style={{ marginTop: "1rem", fontWeight: "700" }}>No Data Found</h4>
              <p className="text-muted">No transactions match the selected criteria.</p>
            </div>
          ) : (
            <>
              {/* Tab 1: Sales Overview */}
              {activeTab === "daily" && (
                <div>
                  <div className={classes.metricsGrid}>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Total Sales</h4>
                        <p className={classes.metricValue}>₹{totalSalesVal.toLocaleString()}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconOrange}`}>
                        <i className="mdi mdi-currency-inr"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Total Orders</h4>
                        <p className={classes.metricValue}>{totalOrdersVal}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconBlue}`}>
                        <i className="mdi mdi-cart-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Delivered Orders</h4>
                        <p className={classes.metricValue}>{deliveredOrdersVal}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconGreen}`}>
                        <i className="mdi mdi-truck-check-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Average Order Value</h4>
                        <p className={classes.metricValue}>₹{Math.round(avgOrderVal).toLocaleString()}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconPurple}`}>
                        <i className="mdi mdi-scale-balance"></i>
                      </div>
                    </div>
                  </div>

                  <Row>
                    <Col lg={12}>
                      <div className={classes.chartContainer}>
                        <div className={classes.chartHeader}>
                          <h3>Sales Trend</h3>
                        </div>
                        <Chart
                          options={salesTrendOptions}
                          series={salesTrendSeries}
                          type="area"
                          height={280}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className={classes.chartContainer}>
                        <div className={classes.chartHeader}>
                          <h3>Orders Trend</h3>
                        </div>
                        <Chart
                          options={ordersTrendOptions}
                          series={ordersTrendSeries}
                          type="area"
                          height={280}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className={classes.tableCard}>
                    <div className={classes.tableTitleRow}>
                      <h3>Daily Sales Logs</h3>
                    </div>
                    <div className={classes.tableWrapper}>
                      <table className={classes.reportTable}>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Orders</th>
                            <th>Sales</th>
                            <th>Avg Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedDates.map((date) => {
                            const origIdx = dailyData.dates.indexOf(date);
                            const count = dailyData.counts[origIdx];
                            const sales = dailyData.sales[origIdx];
                            const avg = count ? Math.round(sales / count) : 0;
                            return (
                              <tr key={date}>
                                <td className={classes.boldCell}>{date}</td>
                                <td>{count}</td>
                                <td className={classes.boldCell}>₹{sales.toLocaleString()}</td>
                                <td>₹{avg.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', background: 'white', borderTop: '1px solid #e9ecef' }}>
                        <span className="text-muted small">
                          Showing Page {dailyLogPage} of {totalPages} ({totalDailyLogs} total rows)
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={dailyLogPage === 1}
                            onClick={() => setDailyLogPage((p) => Math.max(p - 1, 1))}
                          >
                            Previous
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={dailyLogPage === totalPages}
                            onClick={() => setDailyLogPage((p) => Math.min(p + 1, totalPages))}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab 2: Store Performance */}
              {activeTab === "store" && (
                <div>
                  <div className={classes.metricsGrid}>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Highest Revenue Store</h4>
                        <p className={classes.metricValue}>{highestRevenueStore}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconBlue}`}>
                        <i className="mdi mdi-crown-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Total Stores</h4>
                        <p className={classes.metricValue}>{totalStoresCount}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconOrange}`}>
                        <i className="mdi mdi-store-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Average Store Revenue</h4>
                        <p className={classes.metricValue}>₹{Math.round(avgStoreRevenue).toLocaleString()}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconGreen}`}>
                        <i className="mdi mdi-calculator"></i>
                      </div>
                    </div>
                  </div>

                  <div className={classes.chartContainer}>
                    <div className={classes.chartHeader}>
                      <h3>Store Revenue Comparison</h3>
                    </div>
                    <Chart
                      options={storeCompareChartOptions}
                      series={storeCompareChartSeries}
                      type="bar"
                      height={300}
                    />
                  </div>

                  <div className={classes.tableCard}>
                    <div className={classes.tableTitleRow}>
                      <h3>Store Sales Performance</h3>
                    </div>
                    <div className={classes.tableWrapper}>
                      <table className={classes.reportTable}>
                        <thead>
                          <tr>
                            <th>Store</th>
                            <th>Orders</th>
                            <th>Revenue</th>
                            <th>Avg Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeSalesData.map((store) => (
                            <tr key={store.rank}>
                              <td className={classes.boldCell}>{store.name}</td>
                              <td>{store.orders}</td>
                              <td className={classes.boldCell}>₹{store.sales.toLocaleString()}</td>
                              <td>₹{store.orders ? Math.round(store.sales / store.orders).toLocaleString() : 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 3: Best Sellers */}
              {activeTab === "bestsellers" && (
                <div>
                  <div className={classes.metricsGrid}>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Top Selling Product</h4>
                        <p className={classes.metricValue}>{topSellingProduct}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconOrange}`}>
                        <i className="mdi mdi-pizza"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Units Sold</h4>
                        <p className={classes.metricValue}>{unitsSoldVal}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconGreen}`}>
                        <i className="mdi mdi-check-all"></i>
                      </div>
                    </div>
                  </div>

                  <div className={classes.chartContainer}>
                    <div className={classes.chartHeader}>
                      <h3>Top 10 Products</h3>
                    </div>
                    <Chart
                      options={bestSellersChartOptions}
                      series={bestSellersChartSeries}
                      type="bar"
                      height={340}
                    />
                  </div>

                  <div className={classes.tableCard}>
                    <div className={classes.tableTitleRow}>
                      <h3>Best Sellers List</h3>
                    </div>
                    <div className={classes.tableWrapper}>
                      <table className={classes.reportTable}>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Quantity Sold</th>
                            <th>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bestSellersData.map((product, idx) => (
                            <tr key={idx}>
                              <td className={classes.boldCell}>{product.name}</td>
                              <td className={classes.boldCell}>{product.quantity}</td>
                              <td>₹{product.revenue.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 4: Delivery Report */}
              {activeTab === "delivery" && (
                <div>
                  <div className={classes.metricsGrid}>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Total Deliveries</h4>
                        <p className={classes.metricValue}>{deliveryStats.total}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconBlue}`}>
                        <i className="mdi mdi-truck-delivery"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Successful Deliveries</h4>
                        <p className={classes.metricValue}>{deliveryStats.successful}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconGreen}`}>
                        <i className="mdi mdi-check-circle-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Failed Deliveries</h4>
                        <p className={classes.metricValue}>{deliveryStats.failed}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconOrange}`}>
                        <i className="mdi mdi-close-circle-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Success Rate</h4>
                        <p className={classes.metricValue}>{deliveryStats.successRate}%</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconPurple}`}>
                        <i className="mdi mdi-percent"></i>
                      </div>
                    </div>
                  </div>

                  <div className={classes.chartContainer}>
                    <div className={classes.chartHeader}>
                      <h3>Delivery Success vs Failure</h3>
                    </div>
                    <Chart
                      options={deliveryChartOptions}
                      series={deliveryChartSeries}
                      type="donut"
                      height={300}
                    />
                  </div>

                  <div className={classes.tableCard}>
                    <div className={classes.tableTitleRow}>
                      <h3>Rider Performance Log</h3>
                    </div>
                    <div className={classes.tableWrapper}>
                      <table className={classes.reportTable}>
                        <thead>
                          <tr>
                            <th>Rider</th>
                            <th>Deliveries</th>
                            <th>Failed</th>
                            <th>Success Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveryStats.riders.map((rider, idx) => (
                            <tr key={idx}>
                              <td className={classes.boldCell}>{rider.name}</td>
                              <td>{rider.total}</td>
                              <td className="text-danger font-weight-bold">{rider.failed}</td>
                              <td className={classes.boldCell}>{rider.rate}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 5: Promotion Report */}
              {activeTab === "promotion" && (
                <div>
                  <div className={classes.metricsGrid}>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Total Discount Given</h4>
                        <p className={classes.metricValue}>₹{promoStats.totalDiscount.toLocaleString()}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconPurple}`}>
                        <i className="mdi mdi-tag-text-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Promotion Usage</h4>
                        <p className={classes.metricValue}>{promoStats.promoUsageCount}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconGreen}`}>
                        <i className="mdi mdi-ticket-percent-outline"></i>
                      </div>
                    </div>
                    <div className={classes.metricCard}>
                      <div className={classes.metricInfo}>
                        <h4>Most Used Promotion</h4>
                        <p className={classes.metricValue}>{promoStats.topCoupon}</p>
                      </div>
                      <div className={`${classes.metricIcon} ${classes.iconOrange}`}>
                        <i className="mdi mdi-star-circle-outline"></i>
                      </div>
                    </div>
                  </div>

                  <div className={classes.chartContainer}>
                    <div className={classes.chartHeader}>
                      <h3>Promotion Usage</h3>
                    </div>
                    <Chart
                      options={promoChartOptions}
                      series={promoChartSeries}
                      type="bar"
                      height={300}
                    />
                  </div>

                  <div className={classes.tableCard}>
                    <div className={classes.tableTitleRow}>
                      <h3>Promotion Usage Details</h3>
                    </div>
                    <div className={classes.tableWrapper}>
                      <table className={classes.reportTable}>
                        <thead>
                          <tr>
                            <th>Promotion Name</th>
                            <th>Promo Code</th>
                            <th>Usage Count</th>
                            <th>Total Discounts Applied</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {promoStats.list.map((promo, idx) => (
                            <tr key={idx}>
                              <td className={classes.boldCell}>{promo.name}</td>
                              <td>{promo.code}</td>
                              <td>{promo.usage}</td>
                              <td className={classes.boldCell}>₹{promo.discount.toLocaleString()}</td>
                              <td>
                                <span className={`${classes.badgeCustom} ${promo.status === "active" ? classes.badgeActive : classes.badgeExpired}`}>
                                  {promo.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Reports;
